import {Typography} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {DeclarationConsentRow, useDeclarationSigner} from '@/features/trust-accounts/fatca-crs';

import type {FatcaCrsFormValues} from '@/features/trust-accounts/activation/packages/compliance/utils';
import type {DeclarationSigner} from '@/features/trust-accounts/fatca-crs';
import type {FC} from 'react';
import type {Control} from 'react-hook-form';

const SIGNATURE_TEXT_KEYS: Record<DeclarationSigner, string> = {
  trustee: 'activation.packages.compliance.fatcaCrs.consents.signatureText',
  onBehalf: 'activation.packages.compliance.fatcaCrs.consents.signatureTextOnBehalf',
  guest: 'activation.packages.compliance.fatcaCrs.consents.signatureTextGuest'
};

export interface FatcaCrsConsentsSectionProps {
  control: Control<FatcaCrsFormValues>;
  lawyerName?: string;
  disabled?: boolean;
}

export const FatcaCrsConsentsSection: FC<FatcaCrsConsentsSectionProps> = ({
  control,
  lawyerName,
  disabled = false
}) => {
  const {t} = useTranslation('trustAccounts');
  const signer = useDeclarationSigner();

  return (
    <>
      {/* Disclosure consents section */}
      <div className="flex flex-col gap-4">
        <Typography weight="semibold" className="whitespace-normal">
          {t('activation.packages.compliance.fatcaCrs.consents.disclosureSectionHeading')}
        </Typography>
        <div className="overflow-hidden rounded-xl border border-border">
          <DeclarationConsentRow
            name="consentDisclosure"
            control={control}
            id="consent-disclosure"
            disabled={disabled}
          >
            {t('activation.packages.compliance.fatcaCrs.consents.disclosureText')}
          </DeclarationConsentRow>
          <DeclarationConsentRow
            name="consentAccuracyUpdate"
            control={control}
            id="consent-accuracy-update"
            hasDivider
            disabled={disabled}
          >
            {t('activation.packages.compliance.fatcaCrs.consents.accuracyUpdateText')}
          </DeclarationConsentRow>
        </div>
      </div>

      {/* Account holder approval section */}
      <div className="flex flex-col gap-4">
        <Typography weight="semibold" className="whitespace-normal">
          {t('activation.packages.compliance.fatcaCrs.consents.holderSectionHeading')}
        </Typography>
        <div className="overflow-hidden rounded-xl border border-border">
          <DeclarationConsentRow
            name="consentSignature"
            control={control}
            id="consent-signature"
            disabled={disabled}
          >
            {t(SIGNATURE_TEXT_KEYS[signer], {lawyerName: lawyerName ?? ''})}
          </DeclarationConsentRow>
        </div>
      </div>
    </>
  );
};
